import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { THERAPISTS } from "@/lib/constants";
import { absoluteUrl } from "@/lib/seo-config";

const teamSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: THERAPISTS.map((t, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Person",
      name: t.name,
      jobTitle: t.role,
      image: t.photo,
      worksFor: { "@type": "MedicalBusiness", name: "Ashlan Clinic" },
      url: absoluteUrl(`/team#${t.slug}`),
      knowsAbout: t.specialties,
    },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
    { "@type": "ListItem", position: 2, name: "Our Team", item: absoluteUrl("/team") },
  ],
};

export default function Team() {
  return (
    <main data-testid="team-page" className="bg-white">
      <SEO
        title="Our Team — Therapists at Ashlan Clinic, Bermuda"
        description="Meet the Therapists at Ashlan Clinic. Fifty-plus years of combined experience across neuromuscular therapy, Shiatsu, reflexology and applied sleep science."
        path="/team"
        schemas={[teamSchema, breadcrumbSchema]}
      />
      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <span className="ash-eyebrow">Our Team</span>
          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-[#080808] max-w-4xl">
            One Vision. One Clinical Standard.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#3C4048] max-w-2xl leading-relaxed">
            Our therapists combine over fifty years of treatment experience
            across neuromuscular therapy, Shiatsu, reflexology and applied
            sleep science.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col gap-20 md:gap-28">
          {THERAPISTS.map((t, i) => (
            <article
              key={t.slug}
              id={t.slug}
              data-testid={`therapist-profile-${i}`}
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-start ${
                i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-5">
                <div className="relative">
                  <div className="absolute -inset-3 border border-[#9DD4D8] -z-10" />
                  <div className="w-full max-w-[360px] md:max-w-none mx-auto aspect-square md:aspect-[4/5] max-h-[520px] bg-[#F5F5F5] flex items-center justify-center overflow-hidden">
                    <img
                      src={t.photo}
                      alt={`${t.name}, ${t.role}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7">
                <span className="ash-eyebrow">
                  Therapist {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight text-[#080808]">
                  {t.name}{" "}
                  <span className="text-[#3A7A8C] font-semibold text-2xl md:text-3xl ml-2">
                    {t.credentials}
                  </span>
                </h2>
                <p className="mt-3 text-lg text-[#3A7A8C] font-semibold uppercase tracking-[0.14em]">
                  {t.role}
                </p>
                <p className="mt-8 text-[#3C4048] text-lg leading-relaxed max-w-2xl">
                  {t.bio}
                </p>

                <div className="mt-10">
                  <div className="ash-eyebrow">Areas of Focus</div>
                  <ul className="mt-4 flex flex-wrap gap-2">
                    {t.specialties.map((sp) => (
                      <li
                        key={sp}
                        data-testid={`therapist-${i}-specialty`}
                        className="px-4 py-2 border border-[#e5e7eb] bg-white text-sm font-semibold text-[#080808]"
                      >
                        {sp}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-10">
                  <a
                    href={t.bookingUrl}
                    data-testid={`therapist-${i}-book-button`}
                    className="ash-btn-primary"
                  >
                    Book with {t.name.split(" ")[0]}
                    <ArrowUpRight size={16} />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
